import { warning } from "@excalidraw/excalidraw/components/icons";
import clsx from "clsx";
import { useEffect, useState } from "react";

import { useAtomValue } from "../app-jotai";
import { useDrawolaT } from "../storage/i18n";

import { collabSessionExpiresAtAtom } from "./Collab";

import "./CollabSessionTimer.scss";

// Re-checks the countdown this often while a session is active. The room's
// own alarm (see `SESSION_MAX_MINUTES` in `workers/collab/src/room-do.ts`)
// is what actually ends the session and sends `room_closing`; this display
// is advisory only, so a coarse tick is enough (the label only ever shows
// whole minutes).
const TICK_MS = 15_000;

// Mirrors the plan doc (docs/superpowers/specs/2026-09-05-realtime-collab-
// cloudflare-do-plan.html, section 3, fourth layer): warn in the last five
// minutes before the room closes itself.
const WARNING_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Shows the active collaboration session's remaining time, switching to a
 * warning style in the last `WARNING_THRESHOLD_MS`. Renders nothing before a
 * session starts, after it ends, or if the reservation carried no usable
 * expiry (e.g. an older mocked reservation in a test). Once the room
 * actually closes, `handleRoomClosing` in `Collab.tsx` takes over with the
 * "session ended" dialog; this component only covers the lead-up to that.
 */
export const CollabSessionTimer = () => {
  const expiresAt = useAtomValue(collabSessionExpiresAtAtom);
  const t = useDrawolaT();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (expiresAt === null) {
      return undefined;
    }
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(interval);
  }, [expiresAt]);

  if (expiresAt === null) {
    return null;
  }

  const remainingMs = expiresAt - now;
  if (remainingMs <= 0) {
    return null;
  }

  const isWarning = remainingMs <= WARNING_THRESHOLD_MS;
  const label =
    remainingMs < 60_000
      ? t("collab.remainingTime.lessThanAMinute")
      : t("collab.remainingTime.minutesLeft", {
          minutes: Math.ceil(remainingMs / 60_000),
        });

  return (
    <div
      className={clsx("collab-session-timer", {
        "collab-session-timer--warning": isWarning,
      })}
    >
      {isWarning && (
        <span className="collab-session-timer__icon">{warning}</span>
      )}
      {label}
    </div>
  );
};

CollabSessionTimer.displayName = "CollabSessionTimer";
